import { isObject } from "@/libraries/helper";
import {
	hasGetUserMedia,
	polyfillGetUserMedia,
	stopMediaStream,
} from "@/libraries/media";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export interface ScreenshotDimensions {
	width: number;
	height: number;
}

interface UseWebcamProps {
	audio?: boolean;
	audioConstraints?: MediaStreamConstraints["audio"];
	forceScreenshotSourceSize?: boolean;
	imageSmoothing?: boolean;
	mirrored?: boolean;
	minScreenshotHeight?: number;
	minScreenshotWidth?: number;
	onUserMedia?: (stream: MediaStream) => void;
	onUserMediaError?: (error: string | DOMException | Error) => void;
	screenshotFormat?: "image/webp" | "image/png" | "image/jpeg";
	screenshotQuality?: number;
	videoConstraints?: MediaStreamConstraints["video"];
	onCaptureEnd?: (file: Blob) => Promise<void>;
}

const useWebcam = (props: UseWebcamProps = {}) => {
	const {
		audio = false,
		audioConstraints,
		videoConstraints,
		forceScreenshotSourceSize = false,
		imageSmoothing = true,
		mirrored = false,
		minScreenshotWidth,
		minScreenshotHeight,
		onUserMedia,
		onUserMediaError,
		screenshotFormat = "image/webp",
		screenshotQuality = 0.92,
		onCaptureEnd,
	} = props;

	const [hasUserMedia, setHasUserMedia] = useState(false);
	const [isVideoActive, setIsVideoActive] = useState(false);
	const [isRecording, setIsRecording] = useState(false);
	const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
	const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
	const [currentDeviceId, setCurrentDeviceId] = useState<string | undefined>(
		undefined,
	);

	const videoRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
	const requestUserMediaId = useRef(0);

	const prevAudioConstraintsRef = useRef(audioConstraints);
	const prevVideoConstraintsRef = useRef(videoConstraints);
	const prevMinScreenshotWidthRef = useRef(minScreenshotWidth);
	const prevMinScreenshotHeightRef = useRef(minScreenshotHeight);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		polyfillGetUserMedia();

		if (!hasGetUserMedia()) {
			onUserMediaError?.("getUserMedia not supported");
			return;
		}

		getVideoDevices();
		requestUserMedia();

		return () => {
			stopAndCleanup();
		};
	}, []);

	useEffect(() => {
		if (!hasGetUserMedia()) {
			onUserMediaError?.("getUserMedia not supported");
			return;
		}

		const audioConstraintsChanged =
			JSON.stringify(audioConstraints) !==
			JSON.stringify(prevAudioConstraintsRef.current);
		const videoConstraintsChanged =
			JSON.stringify(videoConstraints) !==
			JSON.stringify(prevVideoConstraintsRef.current);
		const minScreenshotWidthChanged =
			minScreenshotWidth !== prevMinScreenshotWidthRef.current;
		const minScreenshotHeightChanged =
			minScreenshotHeight !== prevMinScreenshotHeightRef.current;

		if (
			videoConstraintsChanged ||
			minScreenshotWidthChanged ||
			minScreenshotHeightChanged
		) {
			canvasRef.current = null;
			ctxRef.current = null;
		}

		if (audioConstraintsChanged || videoConstraintsChanged) {
			stopAndCleanup();
			if (isVideoActive) {
				requestUserMedia();
			}
		}

		// Update the previous values
		prevAudioConstraintsRef.current = audioConstraints;
		prevVideoConstraintsRef.current = videoConstraints;
		prevMinScreenshotWidthRef.current = minScreenshotWidth;
		prevMinScreenshotHeightRef.current = minScreenshotHeight;
	}, [
		audioConstraints,
		videoConstraints,
		minScreenshotWidth,
		minScreenshotHeight,
		onUserMediaError,
		isVideoActive,
	]);

	const stopAndCleanup = useCallback(() => {
		if (hasUserMedia) {
			stopMediaStream(streamRef.current);
		}
		setHasUserMedia(false);
		setIsVideoActive(false);
		setIsRecording(false);
		if (
			mediaRecorderRef.current &&
			mediaRecorderRef.current.state !== "inactive"
		) {
			mediaRecorderRef.current.stop();
		}
	}, [hasUserMedia]);

	const getScreenshot = useCallback(
		(screenshotDimensions?: ScreenshotDimensions): string | null => {
			if (!hasUserMedia) return null;

			const canvas = getCanvas(screenshotDimensions);
			return canvas?.toDataURL(screenshotFormat, screenshotQuality) ?? null;
		},
		[hasUserMedia, screenshotFormat, screenshotQuality],
	);

	const getCanvas = useCallback(
		(screenshotDimensions?: ScreenshotDimensions): HTMLCanvasElement | null => {
			if (!videoRef.current || !hasUserMedia || !videoRef.current.videoHeight)
				return null;

			if (!ctxRef.current) {
				let canvasWidth = videoRef.current.videoWidth;
				let canvasHeight = videoRef.current.videoHeight;
				if (!forceScreenshotSourceSize) {
					const aspectRatio = canvasWidth / canvasHeight;

					canvasWidth = minScreenshotWidth || videoRef.current.clientWidth;
					canvasHeight = canvasWidth / aspectRatio;

					if (minScreenshotHeight && canvasHeight < minScreenshotHeight) {
						canvasHeight = minScreenshotHeight;
						canvasWidth = canvasHeight * aspectRatio;
					}
				}

				canvasRef.current = document.createElement("canvas");
				canvasRef.current.width = screenshotDimensions?.width || canvasWidth;
				canvasRef.current.height = screenshotDimensions?.height || canvasHeight;
				ctxRef.current = canvasRef.current.getContext("2d");
			}

			const { current: canvas } = canvasRef;
			const { current: ctx } = ctxRef;

			if (ctx && canvas) {
				// adjust the height and width of the canvas to the given dimensions
				canvas.width = screenshotDimensions?.width || canvas.width;
				canvas.height = screenshotDimensions?.height || canvas.height;

				// mirror the screenshot
				if (mirrored) {
					ctx.translate(canvas.width, 0);
					ctx.scale(-1, 1);
				}

				ctx.imageSmoothingEnabled = imageSmoothing;
				ctx.drawImage(
					videoRef.current,
					0,
					0,
					screenshotDimensions?.width || canvas.width,
					screenshotDimensions?.height || canvas.height,
				);

				// invert mirroring
				if (mirrored) {
					ctx.scale(-1, 1);
					ctx.translate(-canvas.width, 0);
				}
			}

			return canvas;
		},
		[
			hasUserMedia,
			forceScreenshotSourceSize,
			minScreenshotWidth,
			minScreenshotHeight,
			mirrored,
			imageSmoothing,
		],
	);

	const requestUserMedia = useCallback(() => {
		const sourceSelected = (
			audioConstraints: boolean | MediaTrackConstraints | undefined,
			videoConstraints: boolean | MediaTrackConstraints | undefined,
		) => {
			const constraints: MediaStreamConstraints = {
				video:
					typeof videoConstraints !== "undefined"
						? {
								...(videoConstraints as MediaTrackConstraints),
								deviceId: currentDeviceId,
							}
						: { deviceId: currentDeviceId },
			};

			if (audio) {
				constraints.audio =
					typeof audioConstraints !== "undefined" ? audioConstraints : true;
			}

			requestUserMediaId.current++;
			const currentRequestUserMediaId = requestUserMediaId.current;

			navigator.mediaDevices
				.getUserMedia(constraints)
				.then((stream) => {
					if (currentRequestUserMediaId === requestUserMediaId.current) {
						handleUserMedia(null, stream);
					} else {
						stopMediaStream(stream);
					}
				})
				.catch((e) => {
					handleUserMedia(e);
				});
		};

		if ("mediaDevices" in navigator) {
			sourceSelected(audioConstraints, videoConstraints);
		} else {
			const optionalSource = (id: string | null) =>
				({ optional: [{ sourceId: id }] }) as MediaTrackConstraints;

			const constraintToSourceId = (constraint: MediaTrackConstraints) => {
				const { deviceId } = constraint;

				if (typeof deviceId === "string") {
					return deviceId;
				}

				if (Array.isArray(deviceId) && deviceId.length > 0) {
					return deviceId[0];
				}

				if (isObject(deviceId) && "ideal" in deviceId) {
					return deviceId.ideal as string;
				}

				return null;
			};

			// @ts-ignore: deprecated api
			MediaStreamTrack.getSources((sources) => {
				let audioSource: string | null = null;
				let videoSource: string | null = null;

				for (const source of sources) {
					if (source.kind === "audio") {
						audioSource = source.id;
					} else if (source.kind === "video") {
						videoSource = source.id;
					}
				}

				const audioSourceId = constraintToSourceId(
					audioConstraints as MediaTrackConstraints,
				);
				if (audioSourceId) {
					audioSource = audioSourceId;
				}

				const videoSourceId = constraintToSourceId(
					videoConstraints as MediaTrackConstraints,
				);
				if (videoSourceId) {
					videoSource = videoSourceId;
				}

				sourceSelected(
					optionalSource(audioSource),
					optionalSource(videoSource),
				);
			});
		}
	}, [audio, audioConstraints, videoConstraints, currentDeviceId]);

	const handleUserMedia = useCallback(
		(err: Error | null, stream?: MediaStream) => {
			if (err || !stream) {
				setHasUserMedia(false);
				setIsVideoActive(false);
				onUserMediaError?.(err as Error);
				return;
			}

			streamRef.current = stream;

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
			}
			setHasUserMedia(true);
			setIsVideoActive(true);
			onUserMedia?.(stream);
		},
		[onUserMedia, onUserMediaError],
	);

	const switchCamera = useCallback(() => {
		const currentIndex = videoDevices.findIndex(
			(device) => device.deviceId === currentDeviceId,
		);
		const nextIndex = (currentIndex + 1) % videoDevices.length;
		const nextDevice = videoDevices[nextIndex].deviceId;
		if (currentDeviceId === nextDevice) {
			return toast.info("No other camera device found", {
				position: "bottom-center",
			});
		}
		setCurrentDeviceId(nextDevice);
		if (isVideoActive) {
			stopAndCleanup();
			requestUserMedia();
		}
	}, [
		videoDevices,
		currentDeviceId,
		isVideoActive,
		stopAndCleanup,
		requestUserMedia,
	]);

	const onStartRecord = useCallback(() => {
		if (streamRef.current) {
			const mediaRecorder = new MediaRecorder(streamRef.current, {
				mimeType: "video/webm",
			});
			mediaRecorderRef.current = mediaRecorder;
			mediaRecorderRef.current.start();
			mediaRecorderRef.current.onstart = () => {
				setIsRecording(true);
			};
			mediaRecorderRef.current.ondataavailable = (e) => {
				if (e.data && e.data.size > 0) {
					setRecordedChunks((prev) => [...prev, e.data]);
				}
			};
		}
	}, []);

	const onStopRecord = useCallback(() => {
		if (
			mediaRecorderRef.current &&
			mediaRecorderRef.current.state !== "inactive"
		) {
			mediaRecorderRef.current.stop();
			setIsRecording(false);
			setRecordedChunks([]);
			mediaRecorderRef.current = null;
		}
	}, []);

	const getVideoDevices = useCallback(async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const videoDevices = devices.filter(
				(device) => device.kind === "videoinput",
			);
			setVideoDevices(videoDevices);
			if (videoDevices.length > 0 && !currentDeviceId) {
				setCurrentDeviceId(videoDevices[0].deviceId);
			}
		} catch (error) {
			console.error("Error enumerating video devices:", error);
		}
	}, [currentDeviceId]);

	useEffect(() => {
		if (mirrored && videoRef.current) {
			videoRef.current.style.transform = "scaleX(-1)";
		}
	}, [mirrored]);

	useEffect(() => {
		if (!isRecording && recordedChunks.length) {
			const blob = new Blob(recordedChunks, { type: recordedChunks[0].type });
			onCaptureEnd?.(blob);
		}
	}, [isRecording, recordedChunks, onCaptureEnd]);

	return {
		videoRef,
		audio,
		isRecording,
		getScreenshot,
		switchCamera,
		onStartRecord,
		onStopRecord,
	};
};

export default useWebcam;
